// src/utils/MimshakDataUtil.ts
import { GroupByReportedHandlerProduct } from '../interfaces/DTO/agent/mimshakGroupBy';
import { MimshakData } from '../interfaces/DTO/mimshakData';
import { LiquidityPeriodBalanceType, Mimshak, PolicyStatus, Product, ProductType } from '../interfaces/DTO/mislaka_xml_models';
import { PensionFeeExpenseType } from '../interfaces/DTO/mislaka_xml_pension_models';
import { UserTaskMimshakData } from '../interfaces/DTO/userTask/UserTask';
import { MimshakService } from './mimshakService';
import { PathFundSelection } from '../hooks/feeLab/useNewProductHandlers';

export interface NewPolicyMimshakParams {
  handlerCode: string;
  productType: number;
  policyNumber: string;
  reportedPeriod: string;
  fundsSum: number;
  selectedPaths: PathFundSelection[];
  savingFee: number | null;
  accumulatedSavings?: { savings_type_code: number; total_savings: number; deposit_type_code: number; total_redemption_value: number }[];
}

export class MimshakDataUtil {
  /**
   * ממיין MimshakData לפי handler_code ו-product_type
   * @param dataList - רשימת MimshakData
   * @returns רשימת MimshakData ממוינת
   */
  static buildNewPolicyMimshak(params: NewPolicyMimshakParams): Mimshak {
    const { handlerCode, productType, policyNumber, reportedPeriod, fundsSum, selectedPaths, savingFee, accumulatedSavings } = params;
    const today = new Date().toISOString().split('T')[0];

    const paths = selectedPaths.length > 0
      ? selectedPaths.map((s: PathFundSelection) => ({
          path_code: s.fund.fund_id,
          path_name: s.fund.fund_name ?? "",
          accumulated_amount: s.amount,
          deposit_percentage: s.depositPercentage,
          management_fee_on_deposit: 0,
          management_fee_on_savings: savingFee ?? 0,
        }))
      : [{
          path_code: 0,
          path_name: "",
          accumulated_amount: fundsSum,
          management_fee_on_deposit: 0,
          management_fee_on_savings: savingFee ?? 0,
        }];

    const managementFeeDetails = savingFee != null ? [{
      expense_type: PensionFeeExpenseType.FROM_SAVINGS,
      management_fee_rate: savingFee,
    }] : undefined;

    return {
      products: [{
        details: {
          handler_code: handlerCode,
          product_type: productType,
          producer_code: handlerCode,
          adjusted_product_type: productType,
        },
        policies_or_accounts: [{
          policy_number: policyNumber,
          status: PolicyStatus.ACTIVE,
          investment_paths: paths,
          management_fee_details: managementFeeDetails,
          program_name: "",
          update_date: reportedPeriod ?? today,
          accumulated_savings: accumulatedSavings ?? [{
            savings_type_code: LiquidityPeriodBalanceType.CAPITAL,
            total_savings: fundsSum,
            deposit_type_code: 0,
            total_redemption_value: 0,
          }],
          liquidity_summaries: [{ liquidity_date: reportedPeriod ?? today, liquidity_amount: fundsSum }],
        }],
      }],
    };
  }

  static buildSavingsPolicyFundIds(selectedPaths: PathFundSelection[]): number[] {
    return selectedPaths.length > 0 ? selectedPaths.map(s => s.fund.fund_id) : [0];
  }

  static sortByHandlerAndProductType(dataList: MimshakData[]): MimshakData[] {
    return [...dataList].sort((a, b) => {
      // מיון לפי handler_code (אלפביתי)
      if (a.handler_code > b.handler_code) return 1;
      if (a.handler_code < b.handler_code) return -1;

      // אם ה-handler_code זהה → מיון לפי product_type (מספרי)
      return a.product_type - b.product_type;
    });
  }

  static getProductsByContactId(flatMimshaks: MimshakData[], contactId: number): Product[] {
    return flatMimshaks
      .filter((m) => m.contact_id === contactId && m.data && Array.isArray(m.data.products))
      .flatMap((m) => m.data!.products);
  }

 static getInsuranceMimshakData(mimshaks: MimshakData[]): MimshakData[] {
  return mimshaks.filter((m) => {
    // בדיקה בסיסית - אם product_type ריק או 0
    if (m.product_type == null || m.product_type === 0) {
      return false;
    }

    // אם יש adjusted_product_type, נבדוק אם הוא בתוך insuranceFundsTypesByAdjustedProductType
    // אחרת, נבדוק אם product_type בתוך insuranceFundsTypes
    const isValidType = m.adjusted_product_type != null
      ? MimshakService.insuranceFundsTypesByAdjustedProductType.includes(m.adjusted_product_type)
      : MimshakService.insuranceFundsTypes.includes(m.product_type);

    return isValidType;
  });
}

static getPensionMimshakData(mimshaks: MimshakData[]): MimshakData[] {
  return mimshaks.filter((m) => {
    // בדיקה בסיסית - אם product_type ריק או 0
    if (m.product_type == null || m.product_type === 0) {
      return false;
    }

    // אם יש adjusted_product_type, נבדוק אם הוא בתוך pensionFundsTypesByAdjustedProductType
    // אחרת, נבדוק אם product_type בתוך pensionFundsTypes
    const isValidType = m.adjusted_product_type != null
      ? MimshakService.pensionFundsTypesByAdjustedProductType.includes(m.adjusted_product_type)
      : MimshakService.pensionFundsTypes.includes(m.product_type);

    if (!isValidType) {
      return false;
    }

    // בדיקה נוספת עבור LEGACY (ללא adjusted_product_type)
    // עבור PURE_SAVINGS_POLICY או PROVIDENT_FUND ללא adjusted_product_type
    if (m.adjusted_product_type == null && (m.product_type === ProductType.PURE_SAVINGS_POLICY || m.product_type === ProductType.PROVIDENT_FUND)) {
      // אם אין data, לא נוכל לבדוק - נחזיר false (מאוד נדיר)
      if (!m.data || !m.data.products || m.data.products.length === 0) {
        return false;
      }

      // נבדוק את המוצר הראשון (כי ב-MimshakData יש מוצר אחד)
      const product = m.data.products[0];
      if (!product || !product.policies_or_accounts || product.policies_or_accounts.length === 0) {
        return false;
      }

      // בדיקת isPureSavingsPolicyBelongsToPensionFunds
      const belongsToPension = MimshakService.isPureSavingsPolicyBelongsToPensionFunds(
        product.policies_or_accounts
      );

      if (!belongsToPension) {
        return false;
      }
    }

    return true;
  });
}

static getLiquidMimshakData(mimshaks: MimshakData[]): MimshakData[] {
  return mimshaks.filter((m) => {
    // בדיקה בסיסית - אם product_type ריק או 0
    if (m.product_type == null || m.product_type === 0) {
      return false;
    }

    // אם יש adjusted_product_type, נבדוק אם הוא בתוך liquidFundsTypesByAdjustedProductType
    // אחרת, נבדוק אם product_type בתוך liquidFundsTypes
    const isValidType = m.adjusted_product_type != null
      ? MimshakService.liquidFundsTypesByAdjustedProductType.includes(m.adjusted_product_type)
      : MimshakService.liquidFundsTypes.includes(m.product_type);

    if (!isValidType) {
      return false;
    }

    // בדיקה נוספת עבור LEGACY (ללא adjusted_product_type)
    // עבור PURE_SAVINGS_POLICY או PROVIDENT_FUND ללא adjusted_product_type
    if (m.adjusted_product_type == null && (m.product_type === ProductType.PURE_SAVINGS_POLICY || m.product_type === ProductType.PROVIDENT_FUND)) {
      // אם אין data, לא נוכל לבדוק - נחזיר false (מאוד נדיר)
      if (!m.data || !m.data.products || m.data.products.length === 0) {
        return false;
      }

      // נבדוק את המוצר הראשון (כי ב-MimshakData יש מוצר אחד)
      const product = m.data.products[0];
      if (!product || !product.policies_or_accounts || product.policies_or_accounts.length === 0) {
        return false;
      }

      // בדיקת isPureSavingsPolicyBelongsToLiquidFunds
      const belongsToLiquid = MimshakService.isPureSavingsPolicyBelongsToLiquidFunds(
        product.policies_or_accounts
      );

      if (!belongsToLiquid) {
        return false;
      }
    }

    return true;
  });
}

  /**
   * ממיין ומחלץ את נתוני Mimshak מתוך MimshakData
   * @param dataList - רשימת MimshakData
   * @returns רשימת Mimshak ממוינת
   */
  static sortAndExtract(dataList: MimshakData[]): Mimshak[] {
    // ✅ מיון באמצעות הפונקציה הייעודית
    const sortedData = this.sortByHandlerAndProductType(dataList);

    // ✅ סינון ומיפוי → חילוץ ה-Mimshak מתוך MimshakData
    return sortedData
      .map((item) => item.data) // שליפת ה-data (Mimshak) מתוך MimshakData
      .filter((item): item is Mimshak => !!item); // סינון ערכים ריקים
  }

  /**
   * @deprecated השתמש ב-sortAndExtract במקום - השם המקורי לא מדויק
   */
  static flatten(dataList: MimshakData[]): Mimshak[] {
    return this.sortAndExtract(dataList);
  }
  static extractAllProducts(mimshakDataArray: MimshakData[]): Product[] {
    return mimshakDataArray
      .filter(item => item.data && Array.isArray(item.data.products))
      .flatMap(item => item.data!.products);
  }
static mergeDuplicateInvestmentPathsByPathCode(mimshak: MimshakData): MimshakData {
  mimshak.data?.products?.forEach((product) => {
    product.policies_or_accounts?.forEach((policy) => {
      if (policy.investment_paths && policy.investment_paths.length > 1) {
        policy.investment_paths = MimshakService.mergeDuplicateInvestmentPathsByPathCode(policy.investment_paths);
      }
    });
  });
  return mimshak;
}

static getTotalFundSum(mimshaks: MimshakData[]): number {
  return mimshaks.reduce((sum, m) => sum + (m.funds_sum || 0), 0);
}
static getLatestPeriod(mimshaks: MimshakData[]): string | null {
  if (!mimshaks.length) return null;
  const sorted = [...mimshaks].map(m => m.reported_period).sort();
  return sorted[sorted.length - 1] || null;
}
  static groupByUserPolicy(
    tasks: UserTaskMimshakData[]
  ): GroupedUserTaskMimshak[] {
    const map = new Map<string, GroupedUserTaskMimshak>();

    for (const task of tasks) {
      const key = `${task.contact_id}-${task.product_type}-${task.policy_number}`;
      if (!map.has(key)) {
        map.set(key, {
          user_id: task.contact_id,
          product_type: task.product_type,
          policy_number: task.policy_number,
          items: [],
        });
      }

      map.get(key)!.items.push(task);
    }

    // ✨ מיון פנימי של כל קבוצת items לפי reported_period בסדר עולה
    const grouped = Array.from(map.values());
    grouped.forEach((group) =>
      group.items.sort((a, b) => a.reported_period.localeCompare(b.reported_period))
    );

    return grouped;
  }

    // ✅ פונקציה סטטית לביצוע GROUP BY לפי תקופה וסוג מוצר
    static groupByReportedHandlerProductPolicyCount(
        data: GroupByReportedHandlerProduct[]
      ): GroupedChartData[] {
        const groupedData = data.reduce<Record<string, Record<number, number>>>(
          (acc, item) => {
            if (!acc[item.reported_period]) {
              acc[item.reported_period] = {};
            }
            if (!acc[item.reported_period][item.product_type]) {
              acc[item.reported_period][item.product_type] = 0;
            }
            acc[item.reported_period][item.product_type] += item.count_policy_number;
            return acc;
          },
          {}
        );
    
        return Object.entries(groupedData).map(([reported_period, productData]) => ({
          reported_period,
          ...Object.fromEntries(
            Object.entries(productData).map(([product_type, value]) => [
              `product_${product_type}`,
              value,
            ])
          ),
        }));
      }
    static getDistinctFundIds(data: MimshakData[]): number[] {
      const fundIdSet = new Set<number>();

      data.forEach(item => {
        if (Array.isArray(item.fund_ids)) {
          item.fund_ids.forEach(id => {
            if (typeof id === 'number') {
              fundIdSet.add(id);
            }
          });
        }
      });

      return Array.from(fundIdSet);
  }
    
      // פונקציה עבור סכום קרנות
      static groupByReportedHandlerProductFundSum(
        data: GroupByReportedHandlerProduct[]
      ): GroupedChartData[] {
        const groupedData = data.reduce<Record<string, Record<number, number>>>(
          (acc, item) => {
            if (!acc[item.reported_period]) {
              acc[item.reported_period] = {};
            }
            if (!acc[item.reported_period][item.product_type]) {
              acc[item.reported_period][item.product_type] = 0;
            }
            acc[item.reported_period][item.product_type] += item.sum_funds_sum;
            return acc;
          },
          {}
        );
    
        return Object.entries(groupedData).map(([reported_period, productData]) => ({
          reported_period,
          ...Object.fromEntries(
            Object.entries(productData).map(([product_type, value]) => [
              `product_${product_type}`,
              value,
            ])
          ),
        }));
      }
}
export interface GroupedChartData {
    reported_period: string;
    [key: `product_${number}`]: number;
  }

  export interface GroupedUserTaskMimshak {
    user_id: number;
    product_type: number;
    policy_number: string;
    items: UserTaskMimshakData[];
  }
  